import { useCallback, useEffect, useState } from "react";

import {
  getSessionCapabilities,
  invalidateSessionCapabilities,
  PlanFeatureKey,
  PlanFeaturesPayload,
  subscribePlanFeaturesChanged,
} from "../services/sessionCapabilitiesService";
import { getAuthSession } from "../services/authService";

export interface UsePlanFeaturesResult {
  features: PlanFeaturesPayload["features"];
  maxServices: number | null;
  servicesCount: number;
  crmMaxPipelines: number | null;
  crmPipelinesCount: number;
  crmMaxOpenDeals: number | null;
  crmOpenDealsCount: number;
  isTrial: boolean;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  isLoading: boolean;
  /** True when Free/paid plan has a hard cap and usage is at or over it. */
  isAtServiceLimit: boolean;
  can: (feature: PlanFeatureKey | string) => boolean;
  refresh: () => Promise<void>;
}

const EMPTY_FEATURES: PlanFeaturesPayload["features"] = {};

/**
 * Effective plan capabilities for the current tenant session.
 * Source: GET /api/v1/session/capabilities — in-memory TTL only (no localStorage).
 */
export const usePlanFeatures = (): UsePlanFeaturesResult => {
  const hasSession = getAuthSession() !== null;
  const [features, setFeatures] =
    useState<PlanFeaturesPayload["features"]>(EMPTY_FEATURES);
  const [maxServices, setMaxServices] = useState<number | null>(null);
  const [servicesCount, setServicesCount] = useState(0);
  const [crmMaxPipelines, setCrmMaxPipelines] = useState<number | null>(null);
  const [crmPipelinesCount, setCrmPipelinesCount] = useState(0);
  const [crmMaxOpenDeals, setCrmMaxOpenDeals] = useState<number | null>(null);
  const [crmOpenDealsCount, setCrmOpenDealsCount] = useState(0);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(hasSession);

  const load = useCallback(async (force = false) => {
    if (getAuthSession() === null) {
      setFeatures(EMPTY_FEATURES);
      setMaxServices(null);
      setServicesCount(0);
      setCrmMaxPipelines(null);
      setCrmPipelinesCount(0);
      setCrmMaxOpenDeals(null);
      setCrmOpenDealsCount(0);
      setIsTrial(false);
      setTrialEndsAt(null);
      setTrialDaysRemaining(null);
      setIsLoading(false);
      return;
    }

    // Silent refresh: keep prior capabilities so PlanGuard/menu do not unmount
    // the current page (e.g. CRM Kanban after dragging a card).
    if (!force) {
      setIsLoading(true);
    }

    try {
      const data = await getSessionCapabilities(force);
      setFeatures(data.plan_features.features ?? EMPTY_FEATURES);
      setMaxServices(data.plan_features.max_services);
      setServicesCount(data.plan_features.services_count ?? 0);
      setCrmMaxPipelines(data.plan_features.crm_max_pipelines ?? null);
      setCrmPipelinesCount(data.plan_features.crm_pipelines_count ?? 0);
      setCrmMaxOpenDeals(data.plan_features.crm_max_open_deals ?? null);
      setCrmOpenDealsCount(data.plan_features.crm_open_deals_count ?? 0);
      setIsTrial(Boolean(data.plan_features.is_trial));
      setTrialEndsAt(data.plan_features.trial_ends_at ?? null);
      setTrialDaysRemaining(data.plan_features.trial_days_remaining ?? null);
    } catch {
      // On silent refresh, keep previous snapshot instead of wiping gates mid-page.
      if (!force) {
        setFeatures(EMPTY_FEATURES);
        setMaxServices(null);
        setServicesCount(0);
        setCrmMaxPipelines(null);
        setCrmPipelinesCount(0);
        setCrmMaxOpenDeals(null);
        setCrmOpenDealsCount(0);
        setIsTrial(false);
        setTrialEndsAt(null);
        setTrialDaysRemaining(null);
      }
    } finally {
      if (!force) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void load(false);

    return subscribePlanFeaturesChanged(() => {
      void load(true);
    });
  }, [load]);

  const can = useCallback(
    (feature: PlanFeatureKey | string): boolean => {
      if (isLoading) {
        // Hide gated surfaces until capabilities resolve (avoids Free-plan menu flash).
        return false;
      }

      if (!(feature in features)) {
        return true;
      }

      return Boolean(features[feature]);
    },
    [features, isLoading]
  );

  const refresh = useCallback(async () => {
    invalidateSessionCapabilities();
    await load(true);
  }, [load]);

  const isAtServiceLimit =
    maxServices !== null && maxServices > 0 && servicesCount >= maxServices;

  return {
    features,
    maxServices,
    servicesCount,
    crmMaxPipelines,
    crmPipelinesCount,
    crmMaxOpenDeals,
    crmOpenDealsCount,
    isTrial,
    trialEndsAt,
    trialDaysRemaining,
    isLoading,
    isAtServiceLimit,
    can,
    refresh,
  };
};

export default usePlanFeatures;
